"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoArticleUpdater = exports.seoArticleUpdaterResultSchema = exports.seoArticleUpdaterArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var seoArticleWriter_1 = require("./seoArticleWriter");
// SEO Article Updater Arguments Schema
exports.seoArticleUpdaterArgsSchema = zod_1.z.object({
    contentInput: zod_1.z
        .string()
        .min(1, 'Content input is required')
        .describe('required: URL of existing article (e.g., https://example.com/blog/post) or full article text content'),
    urlSlug: zod_1.z
        .string()
        .min(1, 'URL slug is required')
        .describe('required: URL slug for the article (e.g., "my-article-title" - used for file naming and organization)'),
    targetKeywords: zod_1.z
        .union([
        zod_1.z
            .string()
            .min(1, 'At least one keyword is required')
            .describe('Comma-separated keywords: "keyword1, keyword2, keyword3"'),
        zod_1.z
            .array(zod_1.z.string())
            .min(1, 'At least one target keyword is required')
            .describe('Array of keywords: ["keyword1", "keyword2", "keyword3"]'),
    ])
        .refine(function (val) {
        if (typeof val === 'string') {
            // Split by comma, trim, and filter out empty strings
            return (val
                .split(',')
                .map(function (s) { return s.trim(); })
                .filter(Boolean).length > 0);
        }
        if (Array.isArray(val)) {
            return val.filter(Boolean).length > 0;
        }
        return false;
    }, { message: 'At least one target keyword is required' })
        .describe('required: New keywords to add/optimize for (e.g., "adobe ecommerce, magento ecommerce" or ["adobe ecommerce", "magento ecommerce"])'),
    writingGuideline: zod_1.z
        .string()
        .optional()
        .describe('optional: Writing style guidelines - tone, voice, target audience, or specific instructions for content updates'),
    internalLinks: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url('Must be a valid URL'),
        anchorText: zod_1.z
            .string()
            .min(1, 'Anchor text is required')
            .optional()
            .describe('Optional anchor text for the link. If not provided, will be auto-generated'),
        context: zod_1.z
            .string()
            .optional()
            .describe('Optional context about where/why this link should be included'),
    }))
        .optional()
        .describe('optional: Manual internal links to include in the updated article. The system will automatically find additional internal links from your site.'),
    enableAutoInternalLinking: zod_1.z
        .boolean()
        .optional()
        .default(true)
        .describe('optional: Whether to automatically discover and add internal links from your site (default: true)'),
    maxInternalLinks: zod_1.z
        .number()
        .min(3)
        .max(15)
        .optional()
        .default(15)
        .describe('optional: Maximum number of internal links to include (3-15, default: 15)'),
    internalLinkingGuidance: zod_1.z
        .string()
        .optional()
        .describe('optional: Custom instructions for internal link selection and placement (e.g., "Focus on product pages", "Prioritize new collections", "Link to educational content")'),
    targetDomain: zod_1.z
        .string()
        .optional()
        .describe('optional: Target domain for internal link discovery (e.g., "example.com")'),
    includeCTAButtons: zod_1.z
        .boolean()
        .optional()
        .default(false)
        .describe('optional: Whether to include CTA buttons distributed throughout the article (default: false)'),
    ctaButtonStyle: zod_1.z
        .enum(['primary', 'secondary', 'outline'])
        .optional()
        .default('primary')
        .describe('optional: Style of CTA buttons - primary (blue), secondary (gray), or outline (transparent with border)'),
    ctaButtonText: zod_1.z
        .array(zod_1.z.string().min(1, 'CTA text cannot be empty'))
        .max(3)
        .optional()
        .describe('optional: Custom CTA button texts (max 3). If not provided, will auto-generate based on internal links'),
    ctaButtonUrls: zod_1.z
        .array(zod_1.z.string().url('Must be a valid URL'))
        .max(3)
        .optional()
        .describe('optional: Custom URLs for CTA buttons (max 3). If not provided, will use internal links or placeholder URLs'),
    enableImageGeneration: zod_1.z
        .boolean()
        .optional()
        .default(false)
        .describe('optional: Whether to generate AI images for the updated article (default: false)'),
    imagePrompt: zod_1.z
        .string()
        .optional()
        .describe('optional: Custom prompt template for image generation (e.g., "professional business photo", "modern minimalist style")'),
    imageStyle: zod_1.z
        .string()
        .optional()
        .describe('optional: Style guidance for generated images (e.g., "photorealistic", "illustration", "digital art", "corporate")'),
});
// Change summary schema
var changeSummarySchema = zod_1.z.object({
    changesMade: zod_1.z.number().min(0),
    seoScoreImprovement: zod_1.z.string().nullish(),
    wordCountChange: zod_1.z.string().nullish(),
    sectionsModified: zod_1.z.number().min(0).nullish(),
    sectionsAdded: zod_1.z.number().min(0).nullish(),
    sectionsRemoved: zod_1.z.number().min(0).nullish(),
});
// Simple content-based change schema
var contentChangeSchema = zod_1.z.object({
    // Change type
    changeType: zod_1.z
        .enum(['replace', 'insert_after', 'insert_before', 'delete'])
        .describe('replace: Find and replace text. insert_after: Insert content after matched text. insert_before: Insert content before matched text. delete: Remove matched text.'),
    // Content matching (find by actual text, not indices)
    findText: zod_1.z
        .string()
        .nullish()
        .describe('Text to find in the article. Should be exact text from the article. Required for all change types.'),
    replaceWith: zod_1.z
        .string()
        .nullish()
        .describe('New text (for replace) or content to insert (for insert_after/insert_before)'),
    // Context for disambiguation (optional but helpful)
    contextBefore: zod_1.z
        .string()
        .nullish()
        .describe('Text appearing before findText, helps if findText appears multiple times'),
    contextAfter: zod_1.z
        .string()
        .nullish()
        .describe('Text appearing after findText, helps if findText appears multiple times'),
    // Metadata
    changeReason: zod_1.z.string().nullish().describe('Explanation of why this change improves SEO'),
    keywordsTargeted: zod_1.z.array(zod_1.z.string()).nullish().describe('Keywords this change targets'),
});
// SEO updates schema
var seoUpdatesSchema = zod_1.z.object({
    metaTitle: zod_1.z
        .object({
        status: zod_1.z.enum(['modified', 'unchanged']),
        oldValue: zod_1.z.string().nullish(),
        newValue: zod_1.z.string().nullish(),
    })
        .nullish(),
    metaDescription: zod_1.z
        .object({
        status: zod_1.z.enum(['modified', 'unchanged']),
        oldValue: zod_1.z.string().nullish(),
        newValue: zod_1.z.string().nullish(),
    })
        .nullish(),
    keywords: zod_1.z
        .object({
        status: zod_1.z.enum(['modified', 'unchanged']),
        added: zod_1.z.array(zod_1.z.string()).nullish(),
        removed: zod_1.z.array(zod_1.z.string()).nullish(),
    })
        .nullish(),
    jsonLd: zod_1.z
        .object({
        status: zod_1.z.enum(['created', 'modified', 'unchanged']),
        oldValue: seoArticleWriter_1.jsonLdBlogPostingSchema.nullish(),
        newValue: seoArticleWriter_1.jsonLdBlogPostingSchema.nullish(),
        changes: zod_1.z.array(zod_1.z.string()).nullish().describe('List of specific changes made to JSON-LD'),
    })
        .nullish()
        .describe('JSON-LD structured data updates for enhanced SEO'),
});
// SEO Article Updater Result Schema (Simplified from article_diff_schema)
exports.seoArticleUpdaterResultSchema = zod_1.z.object({
    summary: changeSummarySchema,
    changes: zod_1.z.array(contentChangeSchema),
    seoUpdates: seoUpdatesSchema,
    metadata: zod_1.z.object({
        originalSlug: zod_1.z.string(),
        sourceUrl: zod_1.z.string().nullish(),
        sourceDomain: zod_1.z.string().nullish(),
        targetKeywords: zod_1.z.array(zod_1.z.string()),
        updatedAt: zod_1.z.string().datetime(),
        processingTime: zod_1.z.number().nullish(),
    }),
    // Multiple format outputs for maximum platform compatibility
    outputs: zod_1.z
        .object({
        html: zod_1.z.string().optional().describe('Clean HTML format for WordPress, Framer, Webflow'),
        markdown: zod_1.z.string().optional().describe('Markdown format for traditional publishing'),
        plaintext: zod_1.z.string().optional().describe('Plain text format with basic formatting'),
        changesHtml: zod_1.z.string().optional().describe('HTML format of changes summary'),
        changesMarkdown: zod_1.z.string().optional().describe('Markdown format of changes summary'),
    })
        .optional(),
    // Internal links information
    internalLinks: zod_1.z
        .object({
        linksAdded: zod_1.z.array(zod_1.z.object({
            url: zod_1.z.string().url(),
            anchorText: zod_1.z.string(),
            context: zod_1.z.string().optional(),
            priority: zod_1.z.enum(['high', 'medium', 'low']).optional(),
            targetSection: zod_1.z.string().optional(),
            isManual: zod_1.z.boolean().optional().describe('Whether this was a manually specified link'),
        })),
        totalUrlsAnalyzed: zod_1.z.number().optional(),
        sitemapMethod: zod_1.z.enum(['sitemap', 'internal_crawl', 'both']).optional(),
        shopifyUrlsIncluded: zod_1.z.number().optional(),
        reasoning: zod_1.z.string().optional(),
    })
        .optional()
        .describe('Internal links automatically added to the updated article'),
    // Backward compatibility fields
    changesDocxUrl: zod_1.z.string().url().nullish(),
    changesMarkdown: zod_1.z.string().nullish(),
    articleMarkdown: zod_1.z.string().nullish(),
    articleDocxUrl: zod_1.z.string().url().nullish(),
    // Generated images information
    generatedImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url(),
        alt: zod_1.z.string(),
        prompt: zod_1.z.string(),
        style: zod_1.z.string().optional(),
        placement: zod_1.z.string().describe('Where the image was placed in the updated article'),
        sectionIndex: zod_1.z.number().optional(),
        filename: zod_1.z.string(),
    }))
        .optional()
        .describe('AI-generated images added to the updated article'),
});
// Tool definition
exports.seoArticleUpdater = (0, common_1.createToolMetadata)({
    id: 'seoArticleUpdater',
    displayName: 'SEO Article Updater',
    displayDescription: 'Update existing articles with new keywords and SEO improvements. Tracks all changes and provides detailed update summaries for content optimization.',
    category: 'ai',
    color: common_1.BRAND_COLORS.AI,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.AI),
    icon: 'text-edit',
    argsSchema: exports.seoArticleUpdaterArgsSchema,
}, exports.seoArticleUpdaterResultSchema);
